import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Berhasil masuk");
      nav("/");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Sisi Kiri: Foto Pemilahan Arsip & Branding Indoraj */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden bg-primary text-primary-foreground">
        <img
          src="/bg-archive.jpg"
          alt="Pemilahan Arsip PT Indoraj"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-30"
        />
        {/* Lapisan gradien agar teks tetap sangat jelas terbaca */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-primary/60 to-primary/40" />

        <div className="relative z-10 flex items-center gap-3.5">
          <div className="h-12 w-12 rounded-xl bg-white p-1.5 shadow-md flex items-center justify-center">
            <img src="/logo-indoraj.png" alt="Logo Indoraj" className="h-full w-auto object-contain" />
          </div>
          <div>
            <p className="font-heading font-bold text-lg leading-tight tracking-wide text-white">INDORAJ</p>
            <p className="text-[11px] text-white/80 uppercase tracking-widest font-medium">Arsip Multiguna</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-3xl font-bold tracking-tight leading-tight text-white">
            AI-Powered Archive Processing & Project Management
          </h1>
          <p className="mt-4 text-white/90 text-sm leading-relaxed">
            Klasifikasi arsip cerdas, pencocokan JRA, prediksi keterlambatan, dan pelaporan eksekutif
            dalam satu platform enterprise yang aman dan teraudit.
          </p>
          <div className="mt-8 flex items-center gap-2 text-xs text-white/80">
            <ShieldCheck className="h-4 w-4 text-white" /> Secure • Auditable • Concurrent-safe
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/60">© 2026 PT Indoraj Arsip Multiguna</div>
      </div>

      {/* Sisi Kanan: Form Login */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onSubmit={submit}
          className="w-full max-w-sm space-y-6"
        >
          {/* Logo untuk tampilan mobile / responsif */}
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img src="/logo-indoraj.png" alt="Logo Indoraj" className="h-9 w-auto object-contain" />
            <div>
              <span className="font-heading font-bold text-base block leading-none">INDORAJ</span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Arsip Multiguna</span>
            </div>
          </div>

          <div className="space-y-1">
            <div className="hidden lg:block mb-3">
              <img src="/logo-indoraj.png" alt="Logo Indoraj" className="h-10 w-auto object-contain" />
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Masuk ke Sistem</h2>
            <p className="text-sm text-muted-foreground">Gunakan kredensial akun Anda.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nama@perusahaan.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full rounded-full h-11 font-semibold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
